import { useCallback, useEffect, useMemo, useState } from 'react';
import i18next from 'i18next';

import { getAuditButtonLabel } from '@commons/HistoryDrawer/utils';
import auditService from '@services/auditLogs';

/**
 * Fetches and manages audit log state for an entity detail page history drawer.
 *
 * @param {{ showErrorMessage?: (message: string) => void, targetId?: string | null }} options
 */
const useAuditHistory = ({ showErrorMessage, targetId } = {}) => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [isAuditLoading, setIsAuditLoading] = useState(false);
  const [isAuditDrawerOpened, setIsAuditDrawerOpened] = useState(false);

  const auditButtonLabel = useMemo(() => getAuditButtonLabel(auditLogs), [auditLogs]);

  const fetchAuditHistory = useCallback(
    async (auditTargetId) => {
      if (!auditTargetId) {
        return;
      }

      setIsAuditLoading(true);

      try {
        const fetchedAuditLogs = await auditService.getTargetIdAuditLogs(auditTargetId);

        setAuditLogs(fetchedAuditLogs);
      } catch {
        if (showErrorMessage) {
          showErrorMessage(i18next.t('AUDIT_EVENTS.ERROR_FETCHING'));
        }
      } finally {
        setIsAuditLoading(false);
      }
    },
    [showErrorMessage],
  );

  const toggleAuditDrawer = useCallback(() => {
    if (!targetId) {
      return;
    }

    setIsAuditDrawerOpened((isOpened) => !isOpened);
  }, [targetId]);

  const closeAuditDrawer = useCallback(() => {
    setIsAuditDrawerOpened(false);
  }, []);

  useEffect(() => {
    if (!isAuditDrawerOpened || !targetId) {
      return;
    }

    fetchAuditHistory(targetId);
  }, [fetchAuditHistory, isAuditDrawerOpened, targetId]);

  return {
    auditLogs,
    auditButtonLabel,
    auditButtonLoading: isAuditLoading,
    isAuditDrawerOpened,
    fetchAuditHistory,
    toggleAuditDrawer,
    closeAuditDrawer,
  };
};

export default useAuditHistory;
